import { useIntl } from 'react-intl';

import { Typography } from '@/components/ui/typography';
import { getTimeZoneAbbr } from '@/utils/time-zone';

const DATE_OPTIONS = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
} as const;

interface CampaignStampProps {
  /** The campaign's stored start or end instant. eg '2026-08-30T14:04:00.000Z' */
  value: string;
  /** The opening's IANA zone; when set, the cell reads the instant there, with its time and zone. */
  timeZone?: string;
}

/** A campaign start or end date, with its time and zone at the opening when one is given. */
export function CampaignStamp({ value, timeZone }: CampaignStampProps) {
  const intl = useIntl();
  const abbr = timeZone ? getTimeZoneAbbr(new Date(value), timeZone) : '';

  // An unknown zone keeps the browser's date alone rather than a time it cannot place.
  if (!timeZone || !abbr) {
    return (
      <Typography variant="subtitle1">
        {intl.formatDate(value, DATE_OPTIONS)}
      </Typography>
    );
  }

  return (
    <div className="flex flex-col gap-0.5 whitespace-nowrap">
      <Typography variant="subtitle1">
        {intl.formatDate(value, { ...DATE_OPTIONS, timeZone })}
      </Typography>
      <Typography variant="small1" className="text-fx-neutral-500">
        {intl.formatTime(value, {
          hour: 'numeric',
          minute: '2-digit',
          timeZone,
        })}{' '}
        <Typography as="span" variant="small2" className="text-fx-neutral-500">
          {abbr}
        </Typography>
      </Typography>
    </div>
  );
}
